import { useRef, useState } from "react";
import { Phone, MapPin, CalendarClock, Navigation, Camera, PenLine, Trash2, Loader2, ImagePlus, CalendarCheck } from "lucide-react";
import { ConfirmVisitDialog } from "@/components/ConfirmVisitDialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChipGroup } from "@/components/ChipGroup";
import { AuthImage } from "@/components/AuthImage";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { api, compressImage, errMsg, fmtDate } from "@/lib/api";
import { mapsLink } from "@/lib/map";
import { SITE_READY, WIRING, WIRING_ROUTE, VOLTAGE, WALL, DOOR_TYPES, DEVICES, EXTRAS, DIRECTIONS, PHOTO_CATS, MEASURES } from "@/lib/constants";

const dark = "bg-panel border-line";
const Block = ({ title, children }) => (
  <div className="space-y-3"><p className="font-bold text-sm text-slate-300">{title}</p>{children}</div>
);

export const StepClient = ({ insp, setInsp }) => {
  const c = insp.customer;
  const maps = mapsLink(c);
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="space-y-4" data-testid="step-client">
      <div className={`rounded-xl border p-5 ${dark}`}>
        <p className="font-heading text-2xl font-bold" data-testid="wizard-customer-name">{c.name}</p>
        <div className="mt-4 space-y-3 text-slate-300">
          <p className="flex items-center gap-2"><MapPin size={16} className="text-brand-sky" />{[c.area, c.address].filter(Boolean).join(" - ") || "-"}</p>
          <p className="flex items-center gap-2"><CalendarClock size={16} className="text-brand-sky" />{fmtDate(insp.scheduled_at)}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-5">
          <a href={`tel:${c.phone}`} data-testid="wizard-call-button" className="h-12 rounded-lg border border-line flex items-center justify-center gap-2 font-bold hover:bg-panel-hover"><Phone size={17} /><span dir="ltr" className="font-mono">{c.phone}</span></a>
          <a href={maps} target="_blank" rel="noreferrer" data-testid="wizard-maps-button" className="h-12 rounded-lg bg-brand-red text-white flex items-center justify-center gap-2 font-bold hover:bg-brand-red-dark"><Navigation size={17} />الخريطة</a>
        </div>
        {insp.status !== "completed" && (
          <button onClick={() => setConfirm(true)} data-testid="agent-confirm-visit-button"
            className="mt-2 w-full h-12 rounded-lg border border-[#25D366]/70 text-[#4ADE80] font-bold flex items-center justify-center gap-2 hover:bg-[#25D366]/10">
            <CalendarCheck size={17} />{insp.confirmation_sent_at ? "تم تأكيد الموعد ✓ إعادة الإرسال" : "تأكيد الموعد للعميل بواتساب"}
          </button>
        )}
        <ConfirmVisitDialog open={confirm} onClose={() => setConfirm(false)} insp={insp} onSent={(d) => setInsp((x) => ({ ...d, photos: x.photos }))} />
      </div>
      {insp.admin_notes && <div className={`rounded-xl border p-4 text-sm ${dark}`}><p className="text-xs text-slate-400 mb-1">ملاحظات الإدارة</p>{insp.admin_notes}</div>}
    </div>
  );
};

export const StepChecklist = ({ ch, set }) => (
  <div className="space-y-7" data-testid="step-checklist">
    <Block title="هل الموقع جاهز للتركيب؟"><ChipGroup options={SITE_READY} value={ch.site_ready} onChange={(v) => set("site_ready", v)} cols="grid-cols-1" testId="site-ready" /></Block>
    <Textarea placeholder="ملاحظات الجاهزية (اختياري)" value={ch.site_notes || ""} onChange={(e) => set("site_notes", e.target.value)} className={dark} data-testid="site-notes-input" />
    <Block title="تمديدات الأسلاك والكهرباء (اختيار متعدد)"><ChipGroup multi options={WIRING} value={ch.wiring} onChange={(v) => set("wiring", v)} testId="wiring" /></Block>
    <Block title="مسار الأسلاك"><ChipGroup options={WIRING_ROUTE} value={ch.wiring_route} onChange={(v) => set("wiring_route", v)} testId="wiring-route" /></Block>
    <Block title="الجهد الكهربائي"><ChipGroup options={VOLTAGE} value={ch.voltage} onChange={(v) => set("voltage", v)} testId="voltage" /></Block>
    <Block title="نوع الجدار"><ChipGroup options={WALL} value={ch.wall_type} onChange={(v) => set("wall_type", v)} cols="grid-cols-3" testId="wall-type" /></Block>
    <Textarea placeholder="ملاحظات الفتحات والمعوقات" value={ch.openings_notes || ""} onChange={(e) => set("openings_notes", e.target.value)} className={dark} data-testid="openings-notes-input" />
  </div>
);

export const StepMeasure = ({ m, set }) => (
  <div className="space-y-7" data-testid="step-measurements">
    <div className="grid grid-cols-2 gap-3">
      {MEASURES.map((f) => (
        <label key={f.id} className={`rounded-xl border p-3 block ${dark} focus-within:border-brand-red`}>
          <span className="text-xs text-slate-400">{f.label} <span className="font-mono text-brand-sky">{f.code}</span>{(f.id === "height" || f.id === "width") && " *"}</span>
          <div className="flex items-baseline gap-1" dir="ltr">
            <input type="number" inputMode="decimal" min="0" value={m[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder="0"
              data-testid={`measure-${f.id}-input`} className="w-full bg-transparent font-mono text-2xl font-bold outline-none py-1 text-left" />
            <span className="text-xs text-slate-500">mm</span>
          </div>
        </label>
      ))}
      <label className={`rounded-xl border p-3 block ${dark}`}>
        <span className="text-xs text-slate-400">عدد الأبواب</span>
        <input type="number" inputMode="numeric" min="1" value={m.quantity || ""} onChange={(e) => set("quantity", e.target.value)} placeholder="—"
          data-testid="measure-quantity-input" className="w-full bg-transparent font-mono text-2xl font-bold outline-none py-1 text-left" dir="ltr" />
      </label>
    </div>
    <Block title="نوع الباب *"><ChipGroup options={DOOR_TYPES} value={m.door_type} onChange={(v) => set("door_type", v)} cols="grid-cols-2 sm:grid-cols-3" testId="door-type" /></Block>
    <Block title="الجهاز (الماتور)">
      <ChipGroup options={DEVICES} value={m.device} onChange={(v) => set("device", v)} cols="grid-cols-2 sm:grid-cols-3" testId="device" />
      {m.device && (
        <label className={`mt-3 rounded-xl border p-3 flex items-center justify-between gap-3 ${dark}`}>
          <span className="text-sm text-slate-300">عدد الأجهزة ({m.device})</span>
          <input type="number" inputMode="numeric" min="1" value={m.device_qty || ""} onChange={(e) => set("device_qty", e.target.value)} placeholder="1"
            data-testid="device-qty-input" className="w-20 bg-transparent font-mono text-xl font-bold outline-none text-center border-b border-line" dir="ltr" />
        </label>
      )}
    </Block>
    <Block title="إضافات (اختيار متعدد)">
      <ChipGroup multi options={EXTRAS} value={m.extras} onChange={(v) => set("extras", v)} cols="grid-cols-1 sm:grid-cols-3" testId="extras" />
      {(m.extras || []).length > 0 && (
        <Textarea placeholder="ملاحظات الإضافات (مقاس الصندوق، مكان الباب الشخصي...)" value={m.extras_notes || ""} onChange={(e) => set("extras_notes", e.target.value)} className={`mt-3 ${dark}`} data-testid="extras-notes-input" />
      )}
    </Block>
    <Block title="اتجاه الفتح"><ChipGroup multi options={DIRECTIONS} value={m.opening_direction} onChange={(v) => set("opening_direction", v)} testId="direction" /></Block>
    <Textarea placeholder="ملاحظات المقاسات" value={m.notes || ""} onChange={(e) => set("notes", e.target.value)} className={dark} data-testid="measure-notes-input" />
  </div>
);

export const StepPhotos = ({ insp, setInsp, readOnly }) => {
  const input = useRef(null);
  const [cat, setCat] = useState(PHOTO_CATS[0]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const upload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", await compressImage(f), "photo.jpg");
        fd.append("category", cat);
        const { data } = await api.post(`/inspections/${insp.id}/photos`, fd);
        setInsp((x) => ({ ...x, photos: [...x.photos, data] }));
      }
      toast.success("تم رفع الصورة");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    await api.delete(`/inspections/${insp.id}/photos/${p.id}`);
    setInsp((x) => ({ ...x, photos: x.photos.filter((y) => y.id !== p.id) }));
  };

  const saved = (p) => setInsp((x) => ({ ...x, photos: x.photos.map((y) => (y.id === p.id ? p : y)) }));

  return (
    <div className="space-y-5" data-testid="step-photos">
      {!readOnly && (
        <>
          <Block title="تصنيف الصورة"><ChipGroup options={PHOTO_CATS} value={cat} onChange={(v) => setCat(v || PHOTO_CATS[0])} cols="grid-cols-2 sm:grid-cols-3" testId="photo-category" /></Block>
          <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="photo-file-input" />
          <button onClick={() => input.current.click()} disabled={busy} data-testid="capture-photo-button"
            className="w-full h-16 rounded-xl border-2 border-dashed border-brand-sky/70 text-brand-sky font-bold flex items-center justify-center gap-2 hover:bg-brand-sky/10 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" /> : <Camera size={22} />} التقاط صورة بالكاميرا
          </button>
        </>
      )}
      {insp.photos.length === 0 && <p className="text-center text-slate-500 text-sm py-6 flex flex-col items-center gap-2"><ImagePlus />لم تُرفع صور بعد</p>}
      <div className="grid grid-cols-2 gap-3" data-testid="photo-grid">
        {insp.photos.map((p) => (
          <div key={p.id} className={`rounded-xl overflow-hidden border ${dark}`} data-testid={`photo-card-${p.id}`}>
            <AuthImage path={p.path} className="aspect-[4/3] w-full object-cover" />
            <div className="p-2 flex items-center gap-1">
              <span className="text-xs font-semibold me-auto truncate">{p.category}{p.annotated && " · ✎"}</span>
              {!readOnly && <button onClick={() => setEditing(p)} aria-label="رسم" data-testid={`annotate-photo-${p.id}`} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-panel-hover text-brand-sky"><PenLine size={16} /></button>}
              {!readOnly && <button onClick={() => remove(p)} aria-label="حذف" data-testid={`delete-photo-${p.id}`} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-panel-hover text-red-400"><Trash2 size={16} /></button>}
            </div>
          </div>
        ))}
      </div>
      <PhotoAnnotator open={!!editing} photo={editing} inspectionId={insp.id} onClose={() => setEditing(null)} onSaved={saved} />
    </div>
  );
};

export const Summary = ({ insp, ch, m }) => (
  <div className={`rounded-xl border p-4 text-sm space-y-2 ${dark}`} data-testid="sign-summary">
    <p className="font-bold text-brand-sky mb-2">ملخص للمراجعة مع العميل</p>
    {MEASURES.map((f) => <p key={f.id} className="flex justify-between"><span className="text-slate-400">{f.label}</span><span className="font-mono font-bold" dir="ltr">{m[f.id] || "-"} mm</span></p>)}
    <p className="flex justify-between"><span className="text-slate-400">نوع الباب</span><span className="font-bold">{m.door_type || "-"}</span></p>
    <p className="flex justify-between"><span className="text-slate-400">الجهاز</span><span className="font-bold" dir="auto">{m.device ? `${m.device} × ${m.device_qty || 1}` : "-"}</span></p>
    <p className="flex justify-between"><span className="text-slate-400">الإضافات</span><span className="font-bold">{(m.extras || []).join("، ") || "-"}</span></p>
    <p className="flex justify-between"><span className="text-slate-400">اتجاه الفتح</span><span className="font-bold">{(m.opening_direction || []).join("، ") || "-"}</span></p>
    <p className="flex justify-between"><span className="text-slate-400">جاهزية الموقع</span><span className="font-bold">{ch.site_ready || "-"}</span></p>
    <p className="flex justify-between"><span className="text-slate-400">عدد الصور</span><span className="font-mono font-bold">{insp.photos.length}</span></p>
  </div>
);

export const SignerInput = ({ value, onChange }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="اسم العميل الموقّع" className={`h-12 ${dark}`} data-testid="signer-name-input" />
);
